import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const DISCIPLINAS = ["Dibujo", "Pintura", "Cerámica", "Art&Wine", "Curso de verano"];

const empty = {
  nombre: "", apellidos: "", email: "", telefono: "",
  especialidades: [], sede_ids: [], activo: true, notas: "",
};

export default function Profesores() {
  const [items, setItems] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [clases, setClases] = useState([]);
  const [claseIds, setClaseIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/profesores").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/clases").then(({ data }) => setClases(data)).catch(() => setClases([]));
  }, []);

  const abrir = (p) => {
    setForm(p ? { ...empty, ...p } : empty);
    setEditId(p?.id || null);
    setClaseIds(p ? clases.filter((c) => c.profesor_id === p.id).map((c) => c.id) : []);
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSede = (id) =>
    set("sede_ids", form.sede_ids.includes(id) ? form.sede_ids.filter((x) => x !== id) : [...form.sede_ids, id]);

  const save = async () => {
    try {
      const payload = { ...form, especialidades: form.especialidades || [] };
      const id = editId
        ? (await api.put(`/profesores/${editId}`, payload), editId)
        : (await api.post("/profesores", payload)).data.id;
      await api.post(`/profesores/${id}/clases`, { clase_ids: claseIds });
      toast.success(editId ? "Profesor actualizado" : "Profesor creado");
      setOpen(false);
      load();
      api.get("/clases").then(({ data }) => setClases(data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/profesores/${id}`);
    toast.success("Profesor eliminado");
    load();
  };

  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "";

  return (
    <div data-testid="profesores-page">
      <PageHeader
        title="Profesores"
        subtitle="Equipo docente de la academia"
        action={
          <Button className="rounded-full font-semibold transition-transform duration-200 hover:-translate-y-[1px]"
            onClick={() => abrir(null)} data-testid="add-profesor-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo profesor
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((p) => (
          <Card key={p.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`profesor-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-bold text-lg leading-tight">{p.nombre} {p.apellidos}</h3>
                <p className="text-xs text-muted-foreground mt-1">{p.email}</p>
              </div>
              <Badge variant={p.activo ? "default" : "secondary"}>{p.activo ? "Activo" : "Inactivo"}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {(p.especialidades || []).map((e) => (
                <Badge key={e} variant="outline" className="rounded-full">{e}</Badge>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {(p.sede_ids || []).map(sedeName).filter(Boolean).join(" · ")}
            </p>
            {p.telefono && <p className="text-sm text-muted-foreground mt-1">{p.telefono}</p>}
            <div className="flex gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => abrir(p)}
                data-testid={`edit-profesor-${p.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-destructive"
                onClick={() => remove(p.id)} data-testid={`delete-profesor-${p.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <p className="text-muted-foreground text-sm">Todavía no hay profesores.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="profesor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar profesor" : "Nuevo profesor"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="profesor-nombre-input" />
              </div>
              <div className="grid gap-2">
                <Label>Apellidos</Label>
                <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="profesor-apellidos-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="profesor-email-input" />
              </div>
              <div className="grid gap-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="profesor-telefono-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Especialidades</Label>
              <div className="flex flex-wrap gap-2">
                {DISCIPLINAS.map((d) => {
                  const puesta = (form.especialidades || []).includes(d);
                  return (
                    <button key={d} type="button" data-testid={`profesor-especialidad-${d}`}
                      onClick={() => set("especialidades", puesta
                        ? form.especialidades.filter((x) => x !== d)
                        : [...(form.especialidades || []), d])}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                        puesta ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:bg-muted"}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Clases asignadas</Label>
              <div className="flex flex-wrap gap-2">
                {clases
                  .filter((c) => !form.sede_ids?.length || form.sede_ids.includes(c.sede_id))
                  .map((c) => {
                    const puesta = claseIds.includes(c.id);
                    return (
                      <button key={c.id} type="button" data-testid={`profesor-clase-${c.id}`}
                        onClick={() => setClaseIds(puesta ? claseIds.filter((x) => x !== c.id) : [...claseIds, c.id])}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                          puesta ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:bg-muted"}`}>
                        {c.nombre}
                      </button>
                    );
                  })}
                {clases.length === 0 && (
                  <p className="text-xs text-muted-foreground">Todavía no hay clases creadas.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Sólo verá en su calendario y en "Pasar lista" las clases marcadas aquí.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Sedes</Label>
              <div className="flex flex-wrap gap-2">
                {sedes.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleSede(s.id)}
                    data-testid={`profesor-sede-${s.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                      form.sede_ids.includes(s.id)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {s.nombre}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Notas</Label>
              <Textarea value={form.notas} onChange={(e) => set("notas", e.target.value)} data-testid="profesor-notas-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Profesor activo</Label>
              <Switch checked={form.activo} onCheckedChange={(v) => set("activo", v)} data-testid="profesor-activo-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} data-testid="save-profesor-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
